import React, { useState, useEffect, useMemo } from "react"
import { useParams, useNavigate } from "react-router-dom"
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Alert,
  Spinner,
} from "react-bootstrap"
import Select from "react-select"
import { useDispatch, useSelector } from "react-redux"

import RichTextEditor from "../../../components/Admin/RichTextEditor"

import {
  createService,
  updateService,
  getServiceById,
  resetServiceState,
  selectServiceState,
} from "../../../features/services/serviceSlice"

import { getMegaMenus } from "../../../features/megaMenuSlice"

// Helpers
const generateLink = (categoryLabel, subLabel) =>
  `/services/${categoryLabel
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")}/${subLabel
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")}`

const extractSlugFromLink = (link) =>
  link.split("/").filter(Boolean).pop() || ""

const initialState = {
  title: "",
  sort_title: "",
  description: "",
  content: "",
  bgImage: null,
  link: "",
  slug: "",
  showOnHome: false,
  category: null,
  subcategory: null,
}

const AddServices = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { id } = useParams()
  const isEdit = Boolean(id)

  const { loading, success, error, service } = useSelector(selectServiceState)
  const {
    list: megaMenus,
    loading: menuLoading,
    error: menuError,
  } = useSelector((state) => state.megaMenu)

  const [formData, setFormData] = useState(initialState)
  const [categoryOptions, setCategoryOptions] = useState([])
  const [subcategoryOptions, setSubcategoryOptions] = useState([])

  // Form validation
  const isFormValid = useMemo(() => {
    const contentText = formData.content.replace(/<(.|\n)*?>/g, "").trim()
    return (
      formData.title.trim() &&
      formData.sort_title.trim() &&
      formData.description.trim() &&
      contentText &&
      formData.category &&
      formData.subcategory
    )
  }, [formData])

  // Load mega menus
  useEffect(() => {
    dispatch(getMegaMenus())
  }, [dispatch])

  // Prepare category options
  useEffect(() => {
    if (!megaMenus) return
    const categories = megaMenus.map((menu) => ({
      label: menu.title || menu.category,
      value: menu.id,
      data: menu,
    }))
    setCategoryOptions(categories)
  }, [megaMenus])

  // Update subcategory options when category changes
  useEffect(() => {
    if (!formData.category?.data?.data?.sections) {
      setSubcategoryOptions([])
      return
    }

    const sections = formData.category.data.data.sections

    const subs = []
    sections.forEach((section) => {
      if (section.items) {
        section.items.forEach((item) => {
          subs.push({
            label: item.label,
            value: item.link, // link as value
            link: item.link,
          })
        })
      }
    })

    setSubcategoryOptions(subs)

    if (
      formData.subcategory &&
      !subs.find((sub) => sub.value === formData.subcategory.value)
    ) {
      setFormData((p) => ({ ...p, subcategory: null, link: "", slug: "" }))
    }
  }, [formData.category])

  // Fetch service for edit
  useEffect(() => {
    if (isEdit) dispatch(getServiceById(id))
  }, [dispatch, isEdit, id])

  // Populate form in edit mode
  useEffect(() => {
    if (isEdit && service) {
      const category = Number(service.category_id)
      const subcategory = service.sub_category_id

      setFormData({
        title: service.title || "",
        sort_title: service.sort_title || "",
        description: service.description || "",
        content: service.article_content || "",
        category,
        subcategory,
        link: service.link,
        slug: service.slug,
        showOnHome: Boolean(service.display_to_home),
        bgImage: null,
      })
    }
  }, [isEdit, service])

  // Navigate after success
  useEffect(() => {
    if (success) {
      dispatch(resetServiceState())
      navigate(
        service?.display_to_home
          ? "/admin/service-slider-data"
          : "/admin/all-services",
      )
    }
  }, [success, dispatch, navigate, service])

  // Handlers
  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]:
        type === "checkbox" ? checked : type === "file" ? files[0] : value,
    }))
  }

  const handleCategoryChange = (category) => {
    if (!category) {
      setFormData((p) => ({
        ...p,
        category: null,
        subcategory: null,
        link: "",
        slug: "",
      }))
      return
    }
    setFormData((p) => ({
      ...p,
      category,
      subcategory: null,
      link: "",
      slug: "",
    }))
  }

  const handleSubcategoryChange = (subcategory) => {
    if (!subcategory) {
      setFormData((p) => ({ ...p, subcategory: null, link: "", slug: "" }))
      return
    }

    const catLabel = formData.category?.label || "category"
    const newLink = generateLink(catLabel, subcategory.label)

    setFormData((p) => ({
      ...p,
      subcategory,
      link: newLink,
      slug: extractSlugFromLink(newLink),
    }))
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    const payload = new FormData()
    payload.append("title", formData.title)
    payload.append("sort_title", formData.sort_title)
    payload.append("description", formData.description)
    payload.append("article_content", formData.content)
    payload.append("link", formData.link)
    payload.append("slug", formData.slug)
    payload.append("display_to_home", formData.showOnHome)

    if (formData.category?.value)
      payload.append("category_id", String(formData.category.value))
    if (formData.subcategory?.link)
      payload.append("sub_category_id", String(formData.subcategory.link))
    if (formData.bgImage) payload.append("bgImage", formData.bgImage)

    isEdit
      ? dispatch(updateService({ id, payload }))
      : dispatch(createService(payload))
  }

  // UI
  return (
    <Container fluid='md'>
      <Row className='justify-content-center'>
        <Col lg={10}>
          <Card className='border-0 shadow-sm'>
            <Card.Body className='p-4'>
              <h4>{isEdit ? "Update Service" : "New Service"}</h4>
              <p className='text-muted mb-4'>
                Enter service details and visibility options.
              </p>

              {error && (
                <Alert variant='danger'>
                  {error.message || "Something went wrong"}
                </Alert>
              )}

              {/* CATEGORY */}
              <Card className='mb-4'>
                <Card.Body>
                  <h6>Category & Subcategory</h6>
                  {menuLoading ? (
                    <Spinner />
                  ) : menuError ? (
                    <Alert variant='danger'>{menuError}</Alert>
                  ) : (
                    <>
                      <Select
                        options={categoryOptions}
                        value={formData.category}
                        onChange={handleCategoryChange}
                        placeholder='Select category...'
                        isClearable
                      />
                      {formData.category && (
                        <Select
                          options={subcategoryOptions}
                          value={formData.subcategory}
                          onChange={handleSubcategoryChange}
                          placeholder='Select sub-category...'
                          isClearable
                          className='mt-2'
                        />
                      )}
                    </>
                  )}
                </Card.Body>
              </Card>

              <Form onSubmit={handleSubmit}>
                {/* BASIC INFO */}
                <Card className='mb-4'>
                  <Card.Body>
                    <h6>Basic Information</h6>

                    <Form.Group className='mb-3'>
                      <Form.Label>Hero Banner Title *</Form.Label>
                      <Form.Control
                        name='sort_title'
                        value={formData.sort_title}
                        onChange={handleChange}
                        disabled={loading}
                        required
                      />
                    </Form.Group>

                    <Form.Group className='mb-3'>
                      <Form.Label>Home Card Title *</Form.Label>
                      <Form.Control
                        name='title'
                        value={formData.title}
                        onChange={handleChange}
                        disabled={loading}
                        required
                      />
                    </Form.Group>

                    <Row>
                      <Col>
                        <Form.Group className='mb-3'>
                          <Form.Label>URL</Form.Label>
                          <Form.Control value={formData.link} disabled />
                        </Form.Group>
                      </Col>
                      <Col>
                        <Form.Group className='mb-3'>
                          <Form.Label>Slug</Form.Label>
                          <Form.Control value={formData.slug} disabled />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Form.Group className='mb-3'>
                      <Form.Label>Description *</Form.Label>
                      <Form.Control
                        as='textarea'
                        rows={3}
                        name='description'
                        value={formData.description}
                        onChange={handleChange}
                        disabled={loading}
                        required
                      />
                    </Form.Group>
                  </Card.Body>
                </Card>

                {/* MEDIA */}
                <Card className='mb-4'>
                  <Card.Body>
                    <h6>Media</h6>
                    <Form.Control
                      type='file'
                      accept='image/*'
                      name='bgImage'
                      onChange={handleChange}
                      disabled={loading}
                    />
                  </Card.Body>
                </Card>

                {/* CONTENT */}
                <Card className='mb-4'>
                  <Card.Body>
                    <h6>Service Content *</h6>
                    <RichTextEditor
                      value={formData.content}
                      onChange={(html) =>
                        setFormData((p) => ({ ...p, content: html }))
                      }
                      disabled={loading}
                    />
                  </Card.Body>
                </Card>

                {/* VISIBILITY */}
                <Card className='mb-4'>
                  <Card.Body>
                    <Form.Check
                      type='switch'
                      label='Show on Home Page'
                      name='showOnHome'
                      checked={formData.showOnHome}
                      onChange={handleChange}
                      disabled={loading}
                    />
                  </Card.Body>
                </Card>

                {/* ACTIONS */}
                <div className='d-flex justify-content-end gap-2'>
                  <Button
                    variant='secondary'
                    onClick={() => setFormData(initialState)}
                    disabled={loading}
                  >
                    Reset
                  </Button>
                  <Button
                    type='submit'
                    variant='dark'
                    disabled={!isFormValid || loading}
                  >
                    {loading ? (
                      <>
                        <Spinner size='sm' /> Saving...
                      </>
                    ) : isEdit ? (
                      "Update Service"
                    ) : (
                      "Create Service"
                    )}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  )
}

export default AddServices
